/**
 * Enquête n°15 — Adapté de l'Étude de Cas CCS n°9
 * (Enseigner la signalisation horizontale et verticale au permis AM.)
 */

import { ShieldCheck, AlertTriangle, Lightbulb, Quote, Users } from 'lucide-react';

export const signalisationAmEnquete = {
  slug: 'signalisation-am',
  themeCode: 'R',
  issueNumber: 15,
  totalIssues: 15,
  series: 'enquetes',
  category: 'Enquête réglementation & signalisation',
  shortTitle: 'Signalisation routière',
  title: "Lire la route, c'est lire les panneaux",
  kicker: 'Enquête n°15 · Série « Témoignages & Sécurité »',
  subtitle:
    "Triangle = danger, rond rouge = interdiction, rond bleu = obligation, carré bleu = indication. 4 catégories, 1 hiérarchie : agent > feu > panneau > marquage > règles. Voici les 10 panneaux essentiels pour le permis AM.",
  heroImage: '/img/magazines/route.jpg',
  heroImageAlt: "Route avec panneau de signalisation triangulaire bordée d'arbres — illustration de la signalisation routière",
  readTimeMin: 8,
  seoDescription:
    "Enquête réglementaire sur la signalisation routière pour permis AM : 4 catégories de panneaux, signalisation horizontale, hiérarchie agent/feu/panneau/marquage, R411-25, R412-19.",
  seoKeywords:
    'panneaux routiers, signalisation horizontale, signalisation verticale, hiérarchie, R411-25, ligne continue, R412-19, permis AM',

  edito: {
    title: "Le langage qui te sauve la vie",
    body: "La route te parle constamment, dans un langage que tu n'as jamais vraiment appris — même avec l'ASSR2 en poche. <strong>Triangle pointe vers le haut = danger qui arrive</strong>. <strong>Rond rouge = interdiction</strong>. <strong>Rond bleu = obligation</strong>. <strong>Carré bleu = indication</strong>. 4 catégories, et derrière elles une <strong>hiérarchie</strong> que la moitié des conducteurs ignore : l'agent prime sur le feu, qui prime sur le panneau, qui prime sur le marquage, qui prime sur les règles générales. Cette enquête condense l'étude de cas CCS n°9 — la signalisation enseignée officiellement aux candidats permis AM en formation.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'Les 4 catégories en 30 secondes',
      paragraphs: [
        "<strong>1. Panneaux de DANGER</strong>. Triangle équilatéral pointe vers le HAUT, fond blanc, bord rouge. Annoncent un obstacle ou une zone à risque : virage dangereux, descente raide, animaux, travaux, ralentisseur. <strong>Action attendue</strong> : ralentir, augmenter la vigilance, anticiper la réaction.",
        "<strong>2. Panneaux d'INTERDICTION</strong>. Cercle rouge sur fond blanc avec pictogramme noir. Limitation de vitesse, sens interdit, interdiction de dépasser, stationnement. <strong>Effet immediate</strong> : tu ne dois pas faire ce qui est représenté. Sanction de non-respect : variable de 35 € à DÉLIT (sens interdit hors agglo = 135 € + 4 pts).",
        "<strong>3. Panneaux d'OBLIGATION</strong>. Cercle bleu sur fond bleu avec pictogramme blanc. Direction obligatoire, vitesse minimale, fin d'interdiction (cercle noir/blanc). <strong>Effet immediate</strong> : tu DOIS faire ce qui est représenté. Ex : panneau D1c « Direction obligatoire à droite » dans un giratoire.",
        "<strong>4. Panneaux d'INDICATION</strong>. Carré ou rectangle bleu/blanc/vert. Information utile non contraignante : parking, station, hôpital, restaurant, direction. <strong>Effet</strong> : zone informative, pas d'obligation directe.",
      ],
      keyPoints: [
        '1. Danger = triangle pointe haut, rouge',
        '2. Interdiction = cercle rouge',
        '3. Obligation = cercle bleu',
        '4. Indication = carré bleu/vert',
      ],
    },
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "<strong>La hiérarchie de la signalisation</strong> est l'un des points les plus mal sus du Code (article R411-25). Dans l'ordre : <strong>Agent > Feu > Panneau > Marquage > Règles générales</strong>. Concrètement : un agent de la circulation qui te fait signe de passer au feu rouge → tu passes (et tu ne te fais pas verbaliser). Un feu en panne avec un panneau « stop » → tu appliques le stop. Un marquage au sol contradictoire avec un panneau → le panneau prime. Cette logique « du plus humain au plus matériel » sauve la vie aux carrefours en travaux (agent qui supplante les feux débranchés). Mémorisé une fois pour toutes, jamais oublié.",
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'La signalisation horizontale (au sol)',
      paragraphs: [
        "<strong>Ligne CONTINUE blanche</strong>. Article R412-19 : <strong>interdit de la franchir ou de la chevaucher</strong>. Sanction : 135 € + 3 points. Exception majeure depuis 2015 (décret R414-4) : tu peux la franchir pour <strong>dépasser un cycliste ou un cavalier</strong>, sous conditions strictes (visibilité, vitesse ≤ limite, distance latérale 1 m agglo / 1,5 m hors).",
        "<strong>Ligne DISCONTINUE blanche</strong>. Franchissement autorisé (dépassement, changement de file, virage à gauche). Plus l'espace entre les tirets est court, plus le danger est proche — indice visuel pour ralentir.",
        "<strong>Lignes JAUNES</strong>. Provisoires (chantier). Elles annulent et remplacent les lignes blanches. Toujours prioritaires.",
        "<strong>Flèches et marquages spéciaux</strong>. Flèche de rabattement (annonce ligne continue), zébras (interdits au stationnement), passage clouté (piétons prioritaires), losange (voie réservée bus/taxi/véhicules électriques), inscriptions au sol (« STOP », « BUS », « 30 »).",
      ],
      keyPoints: [
        'Ligne CONTINUE blanche : interdit (135 € + 3 pts)',
        'Discontinue : franchissement autorisé',
        'Jaune : provisoire, prioritaire',
        'Flèche rabattement = ralentir',
      ],
    },
    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« Le feu vient de passer au orange, j'ai le temps. » <strong>FAUX dans 80 % des cas.</strong> Le feu orange (R412-30) impose <strong>l'arrêt OBLIGATOIRE</strong> sauf si l'arrêt compromet la sécurité (proximité immédiate, freinage brutal déstabilisateur). Sanction non-respect : <strong>135 € + 4 points</strong>. Et mécanique : à 50 km/h, ta distance de freinage en feu orange est de 25 mètres. Tu ne peux pas le « passer à temps » si tu es à plus de 25 m. <strong>Le piéton qui s'engage au feu vert piéton de l'autre côté te trouvera là</strong>. Pour les cyclos, l'orange = freinage progressif dès 50 m avant le feu, pour le passer ou s'arrêter confortablement.",
    },
    {
      type: 'tip',
      icon: Quote,
      title: 'Le conseil du moniteur ECSR',
      body: "« Dans l'étude de cas n°9 (signalisation AM), je fais systématiquement un <strong>jeu de cartes</strong> avec mes 3 élèves : 30 panneaux flashés en 30 secondes, ils classent en 4 piles (danger/interdiction/obligation/indication). Au début : 8/30. Après 1h : 28/30. Le tournant : associer la <strong>FORME</strong> et la <strong>COULEUR</strong> à la fonction, pas le pictogramme lui-même. Mon conseil pour les ados : avant ta session ASSR2, fais le tour de ton quartier en répertoriant chaque panneau croisé. Photo + catégorie + sens. En 1 semaine, tu auras mémorisé 50 panneaux en contexte réel — mieux qu'un livret de 100 vignettes abstraites. »",
    },
  ],

  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '4',
    middle: 'catégories',
    label: "de panneaux : danger (triangle), interdiction (cercle rouge), obligation (cercle bleu), indication (carré). Au-delà : 1 hiérarchie agent > feu > panneau > marquage > règles.",
    source: 'Arrêté du 24/11/1967 — Signalisation routière française',
    accent: '#475569',
  },

  glossary: [
    { term: 'Signalisation verticale', def: 'Ensemble des panneaux fixés sur poteaux ou portiques (danger, interdiction, obligation, indication).' },
    { term: 'Signalisation horizontale', def: 'Marquages peints au sol (lignes, flèches, inscriptions). Complètent la signalisation verticale.' },
    { term: 'Hiérarchie R411-25', def: 'Ordre d\'autorité d\'application : Agent > Feu > Panneau > Marquage > Règles générales. Le supérieur annule l\'inférieur en cas de contradiction.' },
    { term: 'Ligne continue', def: 'Ligne blanche pleine au sol. Franchissement interdit (R412-19). Sanction : 135 € + 3 pts. Exception cycliste/cavalier depuis 2015 (R414-4).' },
    { term: 'Feu orange', def: 'Phase entre vert et rouge. Arrêt obligatoire (R412-30) sauf si l\'arrêt compromet la sécurité. Sanction non-respect : 135 € + 4 pts.' },
    { term: 'Panneau STOP', def: 'Octogone rouge. Arrêt OBLIGATOIRE complet (R415-6), puis céder le passage. Sanction : 135 € + 4 pts.' },
  ],

  ecsrCorner: {
    title: 'Côté formateur ECSR',
    intro:
      "L'étude de cas n°9 traite de l'enseignement de la signalisation horizontale et verticale au permis AM. Public : 3 candidats AM (14-16 ans), niveau collège.",
    points: [
      'Public-cible : 3 candidats permis AM (14-16 ans), ASSR2 acquise ou en cours.',
      'Type d\'intervention : enseigner (formation initiale AM, 2h théoriques).',
      'Durée : 1 heure structurée — accueil + découverte par carrousel d\'images + apports avec classement au tableau + activité jeu de cartes + évaluation QCM.',
      'Méthode : carrousel d\'images, jeu de cartes par catégorie, plan ville en activité, QCM final.',
      "Résultat attendu : l'élève distingue les 4 catégories, lit la signalisation horizontale, connaît la hiérarchie R411-25, identifie 10 panneaux essentiels.",
    ],
    seeCasLink: {
      label: "Voir l'étude de cas CCS n°9 complète (jury ECSR)",
      to: '/enseignant/etudes-de-cas',
      testid: 'see-cas-ccs-9',
    },
  },

  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur les 4 catégories de panneaux, la signalisation horizontale et la hiérarchie. Réussite minimum : 8/10.",
    themeCode: 'R',
    questionsCount: 10,
    passingScore: 8,
  },

  nextIssue: null,
};

export default signalisationAmEnquete;
